import { action, computed, observable } from 'mobx'
import { BrandDTO } from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import { RootStore } from 'store/RootStore'
import { Api, applyBrandQuery, parseQueryParams } from 'utilities'
import { BrandControllerQueryParams } from './BrandsMainStore.types'

export const BRANDS_MAIN_STORE = 'BRANDS_MAIN_STORE'

export class BrandsMainStore {
  private rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable private brandsMap: Map<number, BrandDTO> = new Map()

  @action private setBrandsMap = (brandsMap: Map<number, BrandDTO>) => {
    this.brandsMap = brandsMap
  }

  @action private setBrand = (brand: BrandDTO) => {
    this.brandsMap.set(brand.id, brand)
  }

  getBrands = async (queryParams: BrandControllerQueryParams = {}): Promise<BrandDTO[]> => {
    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this.rootStore[GENERAL_STORE]

    let brands: BrandDTO[]

    setLoadingOn()
    await Api
      .get<BrandDTO[]>(`/brand/all?${parseQueryParams(queryParams)}`)
      .then(data => {
        const brandsMap = new Map<number, BrandDTO>()
        brands = data.map(b => {
          const brand = applyBrandQuery(b, queryParams)
          brandsMap.set(brand.id, brand)
          return brand
        })

        this.setBrandsMap(brandsMap)
        setLoadingOff()
      })
      .catch(handleApiError)

    return brands
  }

  getBrand = async (
    brandId: number,
    queryParams: BrandControllerQueryParams = {}
  ): Promise<BrandDTO> => {
    let brand = this.getBrandById(brandId)

    const shouldLoadBrandFromApi = !(
      brand &&
      (brand.loadContents || !queryParams.loadContents) &&
      (brand.loadProjects || !queryParams.loadProjects)
    )

    if (shouldLoadBrandFromApi) {
      const {
        setLoadingOn,
        setLoadingOff,
        handleApiError
      } = this.rootStore[GENERAL_STORE]

      setLoadingOn()
      await Api
        .get<BrandDTO>(
          `/project/${brandId}/view?${parseQueryParams(queryParams)}`
        )
        .then(data => {
          brand = applyBrandQuery(data, queryParams)
          this.setBrand(brand)
          setLoadingOff()
        })
        .catch(handleApiError)
    }

    return brand
  }

  getBrandById = (brandId: number): BrandDTO => {
    return this.brandsMap.get(brandId)
  }

  @computed get brandsList (): BrandDTO[] {
    return Array.from(this.brandsMap.values())
  }

  @computed get brandsIds (): number[] {
    return Array.from(this.brandsMap.keys())
  }
}
